import React, { useMemo } from "react";
import { Box, Portal, Table, Text, Title } from "@mantine/core";
import moment from "moment";
import { RunReportV2Response } from "../../../../types/reportsV2";
import { formatMetricValue } from "../../../../utils/metricFormat";
import { ReportTableModel } from "../hooks/useReportTable";
import { flattenReportNodes, valueOf } from "../model/reportTree";
import { RunContext } from "../model/runContext";
import styles from "./ReportPrintView.module.css";

export interface ReportPrintViewProps {
  name: string;
  description?: string;
  data: RunReportV2Response;
  table: ReportTableModel;
  /** Date range, filters and grouping of the run. */
  context: RunContext;
}

/**
 * Print-only copy of the report, portaled to <body>: name, then
 * description · date range · printed date, then the whole tree (fully
 * expanded, sorted as on screen) as a plain table with the totals row.
 */
export default function ReportPrintView({
  name,
  description,
  data,
  table,
  context,
}: ReportPrintViewProps) {
  const { columns, index, firstColumnLabel } = table;
  const totals = data.totals.values;
  const rows = useMemo(
    () => flattenReportNodes(table.sortedNodes, data.granularity, table.range),
    [table.sortedNodes, data.granularity, table.range],
  );
  const subtitleParts = [
    description,
    context.range,
    `Printed ${moment().format("MMM D, YYYY")}`,
  ];
  const cells = (values: Array<number | null>) =>
    columns.map((m) => (
      <Table.Td key={m.key} className={styles.alignRight}>
        {formatMetricValue(valueOf(values, index, m.key), m.format)}
      </Table.Td>
    ));
  return (
    <Portal>
      <Box className={styles.printOnly} data-testid="report-print-view">
        <Box className={styles.root}>
          <Title order={1} className={styles.title}>
            {name}
          </Title>
          <Text className={styles.subtitle}>
            {subtitleParts.filter(Boolean).join(" · ")}
          </Text>
          <Text className={styles.subtitle}>
            Filters: {context.filters} · Grouped by: {context.groupedBy}
          </Text>
          <Table className={styles.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{firstColumnLabel}</Table.Th>
                {columns.map((m) => (
                  <Table.Th key={m.key} className={styles.alignRight}>
                    {m.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(({ node, depth, label }, i) => (
                <Table.Tr
                  key={`${i}-${node.key}`}
                  className={[
                    node.children?.length ? styles.group : "",
                    depth === 0 ? styles.depth0 : "",
                  ].join(" ")}
                >
                  <Table.Td
                    className={styles.first}
                    style={{ "--print-depth": depth } as React.CSSProperties}
                  >
                    {label}
                  </Table.Td>
                  {cells(node.values)}
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr className={styles.totalRow}>
                <Table.Td>Total</Table.Td>
                {cells(totals)}
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Box>
      </Box>
    </Portal>
  );
}
